import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useSession } from '../lib/session';
import { useToast } from '../components/Toast';
import { withTimeout } from '../lib/timeout';
import PasswordField from '../components/PasswordField';
import './SignIn.css';

/**
 * Landing page for the forgot-password recovery link. AuthCallback has
 * already exchanged the PKCE code and established a session by the time
 * we get here - we just need a place to set the new password.
 */
export default function ResetPassword() {
  const { isAuthed, loading } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  if (loading) return null;
  // No session = the link expired, was already consumed, or someone hit
  // the URL directly. Bounce them back to sign-in to start over.
  if (!isAuthed) return <Navigate to="/sign-in" replace />;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    if (pw1.length < 8) { setMsg('At least 8 characters.'); return; }
    if (pw1 !== pw2) { setMsg("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { error } = await withTimeout(
        supabase.auth.updateUser({ password: pw1 }),
        15000,
        'Password update',
      );
      if (error) throw error;
      toast.success('Password updated. You\'re signed in.');
      navigate('/', { replace: true });
    } catch (err: any) {
      setMsg(err?.message ?? 'Update failed');
      toast.error(err?.message ?? 'Password update failed.');
      setBusy(false);
    }
  }

  return (
    <div className="signin">
      <aside className="signin__stripe" aria-hidden="true">
        <div className="signin__logo">
          <img className="signin__logo-ring" src="/logos/cc-logo-icon-white.png" alt="" />
          <span className="signin__logo-wordmark">
            <img src="/logos/cc-logo-full-white.png" alt="" />
          </span>
        </div>
      </aside>

      <main className="signin__form-side">
        <div className="signin__inner">
          <p className="kicker signin__eyebrow">Reset password</p>
          <h1 className="signin__title">
            <span>Set a new</span>
            <br />
            <em>password.</em>
          </h1>

          <form className="signin__form" onSubmit={submit}>
            <label htmlFor="rp-pw1" className="visually-hidden">New password</label>
            <PasswordField
              id="rp-pw1"
              autoComplete="new-password"
              required
              minLength={8}
              placeholder="new password"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
            />
            <label htmlFor="rp-pw2" className="visually-hidden">Confirm new password</label>
            <PasswordField
              id="rp-pw2"
              autoComplete="new-password"
              required
              minLength={8}
              placeholder="confirm new password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
            />
            <button
              type="submit"
              className="signin__submit"
              disabled={!pw1 || !pw2 || busy}
            >
              {busy ? 'Updating…' : 'Update password'}
            </button>
            {msg && <p className="signin__status signin__status--error">{msg}</p>}
          </form>
        </div>
      </main>
    </div>
  );
}
